// Renders a ticket's submitted form responses as a clean label/value list
// (Jira Service Management "Details"-style), driven by the request type's
// form_fields schema. File-type fields are skipped (they live in Attachments).
// When `editable` + `onSaveField` are supplied, each value becomes an inline
// editor (used by both the agent ticket view and the customer portal view).
import React, { useState, useEffect } from 'react';
import { PencilSimple, Check, X } from '@phosphor-icons/react';
import RichEditor, { RichEditorContent } from '../RichEditor';

const PRIORITY_OPTS = [
  { label: 'Low', value: 'low' }, { label: 'Medium', value: 'medium' },
  { label: 'High', value: 'high' }, { label: 'Urgent', value: 'urgent' },
];

function optLabel(field, v) {
  const opts = Array.isArray(field.options) ? field.options : [];
  const found = opts.find((o) => (typeof o === 'object' ? o.value : o) === v);
  if (found) return typeof found === 'object' ? (found.label || found.value) : found;
  return String(v);
}

function formatValue(field, value) {
  if (value == null || value === '' || (Array.isArray(value) && value.length === 0)) return '—';
  if (Array.isArray(value)) return value.map((v) => optLabel(field, v)).join(', ') || '—';
  if (field.type === 'checkbox') return value ? 'Yes' : 'No';
  if (field.type === 'date') {
    const d = new Date(value);
    return isNaN(d.getTime()) ? String(value) : d.toLocaleDateString('en-IN', { dateStyle: 'medium' });
  }
  if (field.type === 'select' || field.type === 'radio' || field.type === 'priority') return optLabel(field, value);
  return String(value);
}

const prettify = (k) => String(k).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const isEmpty = (v) => v == null || v === '' || (Array.isArray(v) && v.length === 0);
const INPUT_CLS = 'w-full text-sm border border-zinc-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-300';

// Read-only render of a field value (links URLs, renders rich text, formats the rest).
function ValueDisplay({ field, value }) {
  if (field.type === 'richtext') {
    return isEmpty(value)
      ? <span className="text-sm text-zinc-400 italic">—</span>
      : <RichEditorContent html={value} />;
  }
  if (field.type === 'url' && value && /^https?:\/\//i.test(String(value))) {
    return (
      <a href={String(value)} target="_blank" rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        className="text-sm text-blue-600 hover:underline break-all"
        data-testid={`field-link-${field.key}`}>{String(value)}</a>
    );
  }
  return <span className="text-sm text-zinc-800 whitespace-pre-wrap break-words">{formatValue(field, value)}</span>;
}

function SaveCancel({ saving, onSave, onCancel }) {
  return (
    <div className="flex items-center gap-1.5">
      <button type="button" disabled={saving} onClick={onSave}
        className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
        data-testid="field-save"><Check size={12} weight="bold" /> Save</button>
      <button type="button" disabled={saving} onClick={onCancel}
        className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded text-zinc-500 hover:bg-zinc-100"
        data-testid="field-cancel"><X size={12} /> Cancel</button>
    </div>
  );
}

function EditableValue({ field, value, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setDraft(value); }, [value]);

  const commit = async (val) => {
    setSaving(true);
    try {
      const ok = await onSave(val);
      if (ok !== false) setEditing(false);
    } finally { setSaving(false); }
  };
  const cancel = () => { setDraft(value); setEditing(false); };

  if (!editing) {
    const empty = isEmpty(value);
    return (
      <div onClick={() => setEditing(true)}
        className="group text-left text-sm text-zinc-800 hover:bg-zinc-50 rounded px-1.5 -mx-1.5 py-0.5 w-full flex items-start gap-1.5 cursor-pointer"
        data-testid={`field-edit-${field.key}`}>
        <div className="flex-1 min-w-0">
          {empty
            ? <span className="text-zinc-400 italic">Click to add</span>
            : <ValueDisplay field={field} value={value} />}
        </div>
        <PencilSimple size={12} className="opacity-0 group-hover:opacity-100 text-zinc-400 shrink-0 mt-0.5" />
      </div>
    );
  }

  const t = field.type;
  const testId = `field-input-${field.key}`;

  if (t === 'textarea') {
    return (
      <div className="space-y-1.5">
        <textarea autoFocus rows={3} value={draft || ''} onChange={(e) => setDraft(e.target.value)} className={INPUT_CLS} data-testid={testId} />
        <SaveCancel saving={saving} onSave={() => commit(draft)} onCancel={cancel} />
      </div>
    );
  }
  if (t === 'richtext') {
    return (
      <div className="space-y-1.5" data-testid={testId}>
        <RichEditor value={draft || ''} onChange={setDraft} autoFocus minHeight={120}
          placeholder="Type here… use the toolbar to format" />
        <SaveCancel saving={saving} onSave={() => commit(draft)} onCancel={cancel} />
      </div>
    );
  }
  if (t === 'select' || t === 'radio' || t === 'priority') {
    const raw = (field.options && field.options.length)
      ? field.options
      : (t === 'priority' ? PRIORITY_OPTS : []);
    const opts = raw.map((o) => (typeof o === 'object' ? o : { label: o, value: o }));
    return (
      <select autoFocus value={draft ?? ''} disabled={saving} onChange={(e) => commit(e.target.value)} className={INPUT_CLS} data-testid={testId}>
        <option value="">—</option>
        {opts.map((o, i) => <option key={i} value={o.value}>{o.label || o.value}</option>)}
      </select>
    );
  }
  if (t === 'checkbox') {
    return (
      <select autoFocus value={draft ? 'yes' : 'no'} disabled={saving} onChange={(e) => commit(e.target.value === 'yes')} className={INPUT_CLS} data-testid={testId}>
        <option value="no">No</option>
        <option value="yes">Yes</option>
      </select>
    );
  }
  if (t === 'multiselect') {
    const opts = (field.options || []).map((o) => (typeof o === 'object' ? o : { label: o, value: o }));
    const arr = Array.isArray(draft) ? draft : [];
    const toggle = (v) => setDraft(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
    return (
      <div className="space-y-1.5">
        <div className="space-y-1 border border-zinc-200 rounded p-2 max-h-44 overflow-auto">
          {opts.map((o, i) => (
            <label key={i} className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={arr.includes(o.value)} onChange={() => toggle(o.value)} /> {o.label || o.value}
            </label>
          ))}
        </div>
        <SaveCancel saving={saving} onSave={() => commit(draft)} onCancel={cancel} />
      </div>
    );
  }

  const inputType = t === 'number' ? 'number' : t === 'date' ? 'date' : t === 'email' ? 'email' : t === 'url' ? 'url' : 'text';
  const numMin = Number.isFinite(field.min) ? field.min : 0; // numbers are non-negative by default
  const normalize = (v) => {
    if (t !== 'number') return v;
    if (v === '') return '';
    const n = Number(v);
    if (!Number.isFinite(n)) return '';
    return n < numMin ? numMin : n;
  };
  return (
    <input autoFocus type={inputType} value={draft ?? ''} disabled={saving}
      min={t === 'number' ? numMin : undefined}
      onChange={(e) => setDraft(e.target.value)}
      onKeyDown={(e) => { if (e.key === 'Enter') commit(normalize(draft)); if (e.key === 'Escape') cancel(); }}
      onBlur={() => commit(normalize(draft))}
      className={INPUT_CLS} data-testid={testId} />
  );
}

export default function TicketFields({ fields, formData, editable = false, onSaveField }) {
  const data = formData || {};
  const hasSchema = Array.isArray(fields) && fields.length > 0;
  const visibleFields = hasSchema ? fields.filter((f) => f.type !== 'file') : [];

  if (!hasSchema) {
    const rows = Object.entries(data).map(([k, v]) => ({
      label: prettify(k),
      value: Array.isArray(v) ? v.join(', ') : typeof v === 'boolean' ? (v ? 'Yes' : 'No') : String(v),
    }));
    if (!rows.length) return null;
    return (
      <dl className="divide-y divide-zinc-100" data-testid="ticket-fields">
        {rows.map((r, i) => (
          <div key={i} className="py-2.5 flex gap-4">
            <dt className="text-xs font-medium text-zinc-500 w-52 shrink-0">{r.label}</dt>
            <dd className="text-sm text-zinc-800 flex-1 min-w-0 whitespace-pre-wrap break-words">{r.value}</dd>
          </div>
        ))}
      </dl>
    );
  }

  if (!visibleFields.length) return null;

  return (
    <dl className="divide-y divide-zinc-100" data-testid="ticket-fields">
      {visibleFields.map((f) => (
        <div key={f.key} className="py-2.5 flex gap-4 items-start" data-testid={`ticket-field-${f.key}`}>
          <dt className="text-xs font-medium text-zinc-500 pt-0.5 w-52 shrink-0">{f.label || prettify(f.key)}</dt>
          <dd className="flex-1 min-w-0">
            {editable && onSaveField
              ? <EditableValue field={f} value={data[f.key]} onSave={(v) => onSaveField(f.key, v)} />
              : <ValueDisplay field={f} value={data[f.key]} />}
          </dd>
        </div>
      ))}
    </dl>
  );
}
